import { SoundEffects } from './sound-effects.js';

/**
 * press-sound.js — the press cue for every control in the app.
 *
 * A handful of buttons in the whole product used to tick and the rest were
 * mute, each tick hand-wired at its own call site — so every new screen
 * shipped silent until someone remembered. One document-level listener now owns the cue, and
 * a control ticks by BEING a control, not by being wired.
 *
 * WHAT A CONTROL IS. The tags and ARIA roles a browser already treats as a
 * pressable thing — the same list the full-screen gesture reads to know where
 * NOT to fire. Cards are deliberately not on it: a card is a game object with
 * its own sound vocabulary (draw, play, summon), not a button; a card that
 * carries `role="button"` (deck builder, mulligan, a Foundry chip) is a toggle
 * or a choice and ticks like one.
 *
 * ONE PRESS, ONE TICK. Three things try to make it two:
 *   - a handler forwarding its click to another control (`target.click()`),
 *     which the coalescing window folds into the press that caused it;
 *   - keyboard activation of a role-only control, where the app's own keydown
 *     handler may or may not synthesise a click — the keydown ticks and the
 *     click, if one follows in the same task, is coalesced. Native controls
 *     click themselves, so their keydown is left alone or Space would tick
 *     twice (keydown now, click on release);
 *   - a click that ENDS A DRAG. Pointer-driven surfaces (a Foundry chip, the
 *     pack seal's ribbon) still receive a click after the release, and a drop
 *     is not a press. The gesture's own travel, measured at pointerup, decides
 *     — and only for the click that immediately follows it, so an engine that
 *     reports a keyboard click with pointer-like fields cannot be silenced by
 *     a stale drag.
 *
 * CAPTURE PHASE, SYNCHRONOUS. The tick lands before the handler runs, so a
 * heavy handler — opening the collection blocks the main thread — cannot make
 * it late, and a handler that stops propagation cannot make it disappear.
 *
 * THE OPT-OUT MEANS OWNERSHIP, NOT SILENCE. `data-press-sound="off"` says
 * "this control plays its own press feedback": End Turn ticks only when the
 * turn is actually accepted, and a pack card only when a claim is attempted.
 * tests/unit/ui/pressSoundOwnership.test.js keeps the explicit `play('press')`
 * call sites and the opt-outs in one-to-one correspondence.
 *
 * Contract: tests/unit/ui/pressSound.test.js.
 */
let PressSound = {};

// A re-evaluated module (Jest, hot reload) must not leave the previous
// instance's listeners ticking alongside its own — same seam as sound-effects.
window.__moonfallPressSoundUninstall?.();

PressSound = (function () {
    const CUE = 'press';

    const CONTROLS = [
        'button', 'input[type="button"]', 'input[type="submit"]', 'input[type="reset"]',
        'input[type="checkbox"]', 'input[type="radio"]', 'a[href]', 'summary',
        '[role="button"]', '[role="tab"]', '[role="switch"]', '[role="checkbox"]',
        '[role="radio"]', '[role="link"]', '[role="option"]', '[role="menuitem"]',
        '[role="menuitemcheckbox"]', '[role="menuitemradio"]',
    ].join(',');

    /** Controls the browser activates from the keyboard by firing a click itself. */
    const NATIVELY_ACTIVATED = 'button, a[href], input, summary';

    const OWNS_ITS_CUE = '[data-press-sound="off"]';

    /* A forwarded synthetic click lands within the same task as the press that
       caused it; a second deliberate press is never this close. */
    const COALESCE_MS = 40;

    /* The app's shared tap slop: a gesture that travels further is a drag. */
    const DRAG_SLOP_PX = 12;

    /* The click that ends a pointer gesture is dispatched right after its
       pointerup. A verdict older than this belongs to no click at all. */
    const GESTURE_CLICK_MS = 400;

    /* Where an editable element is the target, Enter and Space are typing. */
    const EDITABLE = 'input, textarea, select, [contenteditable]:not([contenteditable="false"])';
    /* `aria-disabled` on a region disables every control inside it (WAI-ARIA). */
    const DISABLED_REGION = '[aria-disabled="true"]';

    let lastTickAt = -Infinity;
    /** Where the primary pointer went down, so its pointerup can measure travel. */
    let pressOrigin = null;
    /** The last completed gesture's verdict, consumed by the click that follows it. */
    let gesture = null;
    let installed = false;

    /* Monotonic on purpose: a backwards system-clock step would otherwise
       swallow every press until the clock caught up with the last tick. */
    const now = () => performance.now();

    function controlFrom(node) {
        const el = node instanceof Element ? node : node?.parentElement || null;
        return el?.closest?.(CONTROLS) || null;
    }

    function isInert(control) {
        return control.matches(':disabled')
            || Boolean(control.closest(DISABLED_REGION))
            || Boolean(control.closest(OWNS_ITS_CUE));
    }

    function isPressable(node) {
        const control = controlFrom(node);
        return Boolean(control) && !isInert(control);
    }

    /** True when this pointer click is the release of a drag rather than a press. */
    function endsADrag(event) {
        if (!(event.detail > 0) || !gesture) return false;
        const verdict = gesture;
        gesture = null;
        return verdict.dragged && now() - verdict.at <= GESTURE_CLICK_MS;
    }

    /* A click before the player has ever touched the page is plumbing (the QA
       mute guard presses both channel buttons at boot), and audio started from
       it would be refused anyway. Engines without the API get the attempt. */
    function playerHasInteracted() {
        const activation = navigator.userActivation;
        return !activation || activation.hasBeenActive === true;
    }

    function tick() {
        const at = now();
        if (at - lastTickAt < COALESCE_MS || !playerHasInteracted()) return false;
        lastTickAt = at;
        try {
            return SoundEffects?.play?.(CUE) !== false;
        } catch (_) {
            // A missing cue must never break the click it decorates.
            return false;
        }
    }

    function onPointerDown(event) {
        if (event.isPrimary === false) return;
        pressOrigin = { x: event.clientX, y: event.clientY };
        gesture = null;
    }

    function onPointerUp(event) {
        if (event.isPrimary === false || !pressOrigin) return;
        const travelled = Math.hypot(event.clientX - pressOrigin.x, event.clientY - pressOrigin.y);
        gesture = { dragged: travelled > DRAG_SLOP_PX, at: now() };
        pressOrigin = null;
    }

    function onPointerCancel(event) {
        if (event.isPrimary === false) return;
        pressOrigin = null;
        gesture = null;
    }

    function onClick(event) {
        const control = controlFrom(event.target);
        if (!control || isInert(control) || endsADrag(event)) return;
        tick();
    }

    function onKeyDown(event) {
        if ((event.key !== 'Enter' && event.key !== ' ') || event.repeat || event.isComposing) return;
        const target = event.target instanceof Element ? event.target : null;
        if (target?.closest(EDITABLE)) return;
        const control = controlFrom(target);
        if (!control || isInert(control) || control.matches(NATIVELY_ACTIVATED)) return;
        tick();
    }

    // All in the capture phase; the pointer trio is passive (it never cancels).
    /** @type {Array<[string, (event: any) => void, AddEventListenerOptions]>} */
    const LISTENERS = [
        ['pointerdown', onPointerDown, { capture: true, passive: true }],
        ['pointerup', onPointerUp, { capture: true, passive: true }],
        ['pointercancel', onPointerCancel, { capture: true, passive: true }],
        ['click', onClick, { capture: true }],
        ['keydown', onKeyDown, { capture: true }],
    ];

    function install() {
        if (installed) return;
        installed = true;
        for (const [type, handler, options] of LISTENERS) document.addEventListener(type, handler, options);
    }

    function uninstall() {
        if (!installed) return;
        installed = false;
        for (const [type, handler, options] of LISTENERS) document.removeEventListener(type, handler, options);
        pressOrigin = null;
        gesture = null;
        lastTickAt = -Infinity;
        if (window.__moonfallPressSoundUninstall === uninstall) {
            delete window.__moonfallPressSoundUninstall;
        }
    }

    install();
    window.__moonfallPressSoundUninstall = uninstall;

    return { install, uninstall, isPressable };
})();

export { PressSound };
